import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import useReveal from '../../utils/useReveal';
import { FaArrowRight } from 'react-icons/fa';
import {
  CardGrid,
  Container,
  Desc,
  EmptyState,
  ExploreMore,
  FilterBar,
  FilterButton,
  Header,
  HeaderRow,
  Highlight,
  SectionLabel,
  Title,
  Wrapper,
} from './ProjectsStyle';
import ProjectCard from '../Cards/ProjectCards';
import { projectCatalog } from '../../data/projectCatalog';
import { matchesFilter, projectFilters } from '../../utils/projectHelpers';

const Projects = ({ setOpenModal }) => {
  const reveal = useReveal();
  const [toggle, setToggle] = useState('all');
  const [showAllProjects, setShowAllProjects] = useState(false);

  const filteredProjects = useMemo(
    () => projectCatalog.filter((project) => matchesFilter(project, toggle)),
    [toggle]
  );

  const featuredProjects = filteredProjects.filter(project => project.isFeatured);
  const moreProjects = filteredProjects.filter(project => !project.isFeatured);

  const openProject = (payload) => {
    const project = payload?.project || payload;
    setOpenModal({ state: true, project });
  };

  const handleFilterChange = (filter) => {
    setToggle(filter);
    setShowAllProjects(false);
  };

  return (
    <Container id="projects">
      <Wrapper>
        <HeaderRow>
          <Header as={motion.div} {...reveal}>
            <SectionLabel>My Work</SectionLabel>
            <Title>Selected <Highlight>Engineering Work</Highlight></Title>
            <Desc>These projects show how I build APIs, connect applications with databases and AI services, and turn practical problems into working software.</Desc>
          </Header>

          <FilterBar role="group" aria-label="Project filters">
            {projectFilters.map((filter) => (
              <FilterButton
                as={motion.button}
                whileTap={{ scale: 0.98 }}
                key={filter.id}
                type="button"
                $active={toggle === filter.id}
                aria-pressed={toggle === filter.id}
                onClick={() => handleFilterChange(filter.id)}
              >
                {filter.label}
              </FilterButton>
            ))}
          </FilterBar>
        </HeaderRow>

        <p role="status" aria-live="polite">{filteredProjects.length} projects match this filter</p>
        {featuredProjects.length > 0 && <h3>Featured Projects</h3>}
        {featuredProjects.length > 0 && (
          <CardGrid>
            {featuredProjects.map((project, index) => (
              <ProjectCard index={index} key={project.title} project={project} setOpenModal={openProject} />
            ))}
          </CardGrid>
        )}
        {!filteredProjects.length && <EmptyState>No projects match this filter.</EmptyState>}
        {moreProjects.length > 0 && (
          <>
            <ExploreMore type="button" aria-expanded={showAllProjects} aria-controls="more-projects" onClick={() => setShowAllProjects(value => !value)}>
              {showAllProjects ? 'Collapse More Projects' : `More Projects (${moreProjects.length})`} <FaArrowRight />
            </ExploreMore>
            <div id="more-projects" hidden={!showAllProjects}>
              <h3 style={{ marginBottom: 20 }}>More Projects</h3>
              <CardGrid>{showAllProjects && moreProjects.map((project, index) => <ProjectCard key={project.title} project={project} setOpenModal={openProject} />)}</CardGrid>
            </div>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

export default Projects;
